import { localDateToDate } from "@flockcover/flock-svc-core-types"
import groupBy from "lodash.groupby"
import partition from "lodash.partition"
import { ResolvedFleetEntry } from "../../../api/fleet"
import { sanitiseReg } from "../../utils"

const defaultEndDate = new Date(`5000-01-01`) // I want this code to last this long

type Range = { start: Date; end: Date }
const dateRangeOverlaps = (a: Range, b: Range) => {
  if (a.start <= b.start && b.start <= a.end) {
    return true
  } // b starts in a
  if (a.start <= b.end && b.end <= a.end) {
    return true
  } // b ends in a
  if (b.start < a.start && a.end < b.end) {
    return true
  } // a in b
  return false
}

const getDatesToCompare = (entry: ResolvedFleetEntry) => ({
  start: localDateToDate(entry.onCoverDate, `Europe/London`),
  end: entry.offCoverDate ? localDateToDate(entry.offCoverDate, `Europe/London`) : defaultEndDate,
})

const entryDatesOverlap = (base: ResolvedFleetEntry, compare: ResolvedFleetEntry) => {
  const baseDates = getDatesToCompare(base)
  const compareDates = getDatesToCompare(compare)
  return dateRangeOverlaps(baseDates, compareDates)
}

const isEntryNonNull = (entry: ResolvedFleetEntry | null): entry is ResolvedFleetEntry => entry != null

const resolveEntryGroup = (entries: ResolvedFleetEntry[]) => {
  const intersectingGroups = entries.reduce((currentGroups, entry) => {
    const [matchingGroups, nonMatchingGroups] = partition(currentGroups, (group) =>
      group.some((otherEntry) => entryDatesOverlap(entry, otherEntry))
    )
    if (matchingGroups.length) {
      return [...nonMatchingGroups, [...matchingGroups.flat(), entry]]
    }
    return [...currentGroups, [entry]]
  }, [] as ResolvedFleetEntry[][])

  return intersectingGroups
    .map((group) =>
      group.reduce((representativeVehicle, currentVec) => {
        if (!representativeVehicle) {
          if (currentVec.offCoverDate === undefined) currentVec.offCoverDate = null
          return currentVec
        }
        const representationToReturn: ResolvedFleetEntry = { ...representativeVehicle }
        if (
          localDateToDate(currentVec.onCoverDate, `Europe/London`) <
          localDateToDate(representationToReturn.onCoverDate, `Europe/London`)
        ) {
          representationToReturn.onCoverDate = currentVec.onCoverDate
        }
        if (!currentVec.offCoverDate) {
          representationToReturn.offCoverDate = null
        } else if (
          !!representationToReturn.offCoverDate &&
          localDateToDate(currentVec.offCoverDate, `Europe/London`) >
            localDateToDate(representationToReturn.offCoverDate, `Europe/London`)
        ) {
          representationToReturn.offCoverDate = currentVec.offCoverDate
        }
        return representationToReturn
      }, null as ResolvedFleetEntry | null)
    )
    .filter(isEntryNonNull)
}

/**
 * Turns a vehicle list into the most simple representation based on VRN and cover dates
 */
export const resolveCoverPeriods = (entries: ResolvedFleetEntry[]) => {
  const byVrn = groupBy(entries, (it) => sanitiseReg(it.registrationNumber))
  return Object.values(byVrn).flatMap(resolveEntryGroup)
}
